import { Button, Group, List, Stack, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';

import { Batch, BatchStatistics, BatchTypeEnum, ReportStatus } from '@e-origin/shared';

import { useDownloadBulkBatchesReport } from '@hooks/batch';

export interface GenerateBulkCustomsReportModalProps {
  onClose: () => void;
  selectedBatchIds: string[];
  batchesData: Batch[];
  batchView: BatchTypeEnum;
}

const isBatchReadyForReport = (statistics: BatchStatistics) => {
  const isFullyProcessed = statistics?.total === statistics?.released || statistics?.total === statistics?.exported;

  const allowedStatusesSum =
    (statistics?.notReleased || 0) +
    (statistics?.released || 0) +
    (statistics?.exported || 0) +
    (statistics?.accepted || 0) +
    (statistics?.inControl || 0) +
    (statistics?.invalidated || 0);

  const isBatchReportAvailable =
    allowedStatusesSum === statistics?.total && ((statistics?.notReleased || 0) > 0 || (statistics?.inControl || 0) > 0 || (statistics?.invalidated || 0) > 0);

  return isFullyProcessed || isBatchReportAvailable;
};

export const GenerateBulkCustomsReportModal: React.FC<GenerateBulkCustomsReportModalProps> = ({ onClose, selectedBatchIds, batchesData, batchView }) => {
  const navigation = useNavigate();
  const downloadBulkBatchesReportMutation = useDownloadBulkBatchesReport();
  const selectedBatches = batchesData.filter((b) => selectedBatchIds.includes(b._id));
  const downloadableBatches = selectedBatches.filter((b) => b.reports?.customsReport?.status === ReportStatus.DONE && isBatchReadyForReport(b.statistics));

  const notFullyReleased = selectedBatches.filter((b) => !isBatchReadyForReport(b.statistics));

  const readyForReport = selectedBatches.filter((b) => b.reports?.customsReport?.status !== ReportStatus.DONE && isBatchReadyForReport(b.statistics));

  const handleDownloadReports = () => {
    downloadBulkBatchesReportMutation.mutate({ batchIds: downloadableBatches.map((b) => b._id) }, { onSuccess: onClose });
  };

  return (
    <Stack>
      <Text>
        You can generate bulk reports only for fully {batchView === BatchTypeEnum.HIGH_VALUES_EXPORT ? 'exported' : 'released'} or in control / invalidated /
        not released batches.
      </Text>
      {notFullyReleased.length > 0 && (
        <Stack>
          <Text c="red">
            The following batches are not fully {batchView === BatchTypeEnum.HIGH_VALUES_EXPORT ? 'exported' : 'released'} or in control / invalidated / not
            released:
          </Text>
          <List>
            {notFullyReleased.map((b) => (
              <List.Item key={b._id}>{b.name}</List.Item>
            ))}
          </List>
        </Stack>
      )}
      {readyForReport.length > 0 && (
        <Stack>
          <Text c="orange">The following batches are ready for report generation but have no report:</Text>
          <List>
            {readyForReport.map((b) => (
              <List.Item key={b._id}>{b.name}</List.Item>
            ))}
          </List>

          <Text>To generate the reports for these batches, please go to Manage Account.</Text>
          <Group justify="center">
            <Button
              variant="outline"
              onClick={() => {
                navigation('/manage-account/customs-reports');
              }}
            >
              Go to Manage Account
            </Button>
          </Group>
        </Stack>
      )}

      {downloadableBatches.length > 0 && (
        <Stack>
          <Text>{downloadableBatches.length} reports are ready. Are you sure you want to download them?</Text>
          <Group justify="center">
            <Button type="button" variant="transparent" onClick={onClose} size="lg">
              Cancel
            </Button>
            <Button type="submit" variant="filled" size="lg" onClick={handleDownloadReports}>
              Download reports
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
};
